import { NetworkStatus, useApolloClient, useQuery } from "@apollo/client";
import { Alert, Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { useSearchParams } from "react-router-dom";

import { CursorPagination } from "../components/CursorPagination";
import { FinanceNotificationCard } from "../components/finance/FinanceNotificationCard";
import { RequirePermission } from "../components/RequirePermission";
import { ResourceName } from "../constants/resources";
import {
  FinanceNotificationRule,
  FinanceNotificationsDocument,
  type FinanceNotificationsQuery,
  type FinanceNotificationsQueryVariables,
} from "../generated/graphql";
import { formatCrmDateTime } from "../lib/crm";
import { isFinanceNotificationRule } from "../lib/financeNotifications";
import { typography } from "../theme/designTokens";

const PAGE_SIZE = 50;
type NotificationPagination = {
  response?: FinanceNotificationsQuery;
  page: number;
  cursors: (string | null)[];
  terminalPage: number | null;
};

// The order the rules run in.
const RULES = [
  FinanceNotificationRule.BillingStartDue,
  FinanceNotificationRule.BillingStopNeeded,
  FinanceNotificationRule.ContractChangeDue,
  FinanceNotificationRule.CancellationWithdrawn,
  FinanceNotificationRule.EndDateChanged,
];

const FinanceNotifications = ({
  rule,
  onRuleChange,
}: {
  rule: FinanceNotificationRule | null;
  onRuleChange: (rule: FinanceNotificationRule | null) => void;
}) => {
  const translate = useTranslate();
  const client = useApolloClient();
  const { canAccess: canDismiss } = useCanAccess({
    action: "list",
    resource: ResourceName.FinanceNotificationHandling,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [pagination, setPagination] = useState<NotificationPagination>({
    page: 1,
    cursors: [null],
    terminalPage: null,
  });
  const { page, cursors, terminalPage } = pagination;
  const inFlight = useRef(false);
  const requestGeneration = useRef(0);
  const baseRefetching = useRef(false);
  const variables: FinanceNotificationsQueryVariables = { rule };
  const {
    data: baseData,
    loading,
    error,
    networkStatus,
  } = useQuery<FinanceNotificationsQuery, FinanceNotificationsQueryVariables>(FinanceNotificationsDocument, {
    variables,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });
  if (networkStatus === NetworkStatus.refetch && !baseRefetching.current) {
    baseRefetching.current = true;
    requestGeneration.current += 1;
    inFlight.current = false;
    setLoadingMore(false);
  } else if (networkStatus !== NetworkStatus.refetch && baseRefetching.current) {
    baseRefetching.current = false;
    if (networkStatus === NetworkStatus.ready && !error && baseData) {
      setPagination({ page: 1, cursors: [null], terminalPage: null });
      setMoreFailed(false);
    }
  }
  const data = pagination.response ?? baseData;
  const list = data?.financeNotifications;
  const variablesKey = JSON.stringify(variables);
  const shownKey = useRef<string | null>(variablesKey);
  shownKey.current = variablesKey;
  useEffect(() => {
    shownKey.current = variablesKey;
    return () => {
      shownKey.current = null;
    };
  }, [variablesKey]);
  const hasNextPage = Boolean(list?.hasNextPage && terminalPage !== page);

  const changePage = async (next: number) => {
    if (inFlight.current || loading || !list || next === page || next < 1) return;
    const newPage = next === page + 1 && next === cursors.length + 1;
    const after = newPage ? list.endCursor : cursors[next - 1];
    if (after === undefined || (newPage && (!hasNextPage || !after))) return;
    const requested = variablesKey;
    const generation = requestGeneration.current;
    inFlight.current = true;
    setLoadingMore(true);
    setMoreFailed(false);
    const requestPage = (cursor: string | null) =>
      client.query<FinanceNotificationsQuery, FinanceNotificationsQueryVariables>({
        query: FinanceNotificationsDocument,
        variables: { ...variables, after: cursor },
        fetchPolicy: "no-cache",
      });
    try {
      const result = await requestPage(after);
      if (requested !== shownKey.current || generation !== requestGeneration.current) return;
      if (next > page && result.data.financeNotifications.nodes.length === 0) {
        setPagination(current => ({ ...current, terminalPage: page }));
        return;
      }
      if (next > 1 && result.data.financeNotifications.nodes.length === 0) {
        const first = await requestPage(null);
        if (requested !== shownKey.current || generation !== requestGeneration.current) return;
        setPagination({ response: first.data, page: 1, cursors: [null], terminalPage: null });
        return;
      }
      setPagination({
        response: result.data,
        page: next,
        cursors: newPage ? [...cursors, after] : cursors,
        terminalPage,
      });
    } catch {
      if (requested === shownKey.current && generation === requestGeneration.current) setMoreFailed(true);
    } finally {
      if (requested === shownKey.current && generation === requestGeneration.current) {
        inFlight.current = false;
        setLoadingMore(false);
      }
    }
  };

  const title = translate("financeNotifications.title");

  return (
    <Stack gap={2} maxWidth={960} data-testid="finance-notifications">
      <Title title={title} />
      <Box>
        <Typography variant="overline" color="text.secondary">
          {translate("financeNotifications.section")}
        </Typography>
        <Typography component="h1" variant="h5" fontWeight={typography.fontWeight.semibold}>
          {title}
        </Typography>
        {list && (
          <Typography color="text.secondary">
            {translate("financeNotifications.count", { smart_count: list.totalCount })}
            {" • "}
            {list.sync.lastSucceededAt
              ? translate("financeNotifications.lastSync", { time: formatCrmDateTime(list.sync.lastSucceededAt) })
              : translate("financeNotifications.noSync")}
          </Typography>
        )}
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={1} role="group" aria-label={translate("financeNotifications.filter")}>
        {[null, ...RULES].map(option => (
          <Chip
            key={option ?? "all"}
            label={translate(option ? `financeNotifications.rules.${option}` : "financeNotifications.all")}
            color={option === rule ? "primary" : "default"}
            variant={option === rule ? "filled" : "outlined"}
            aria-pressed={option === rule}
            onClick={() => onRuleChange(option)}
          />
        ))}
      </Stack>
      {list?.sync.lastRunFailed && <Alert severity="warning">{translate("financeNotifications.syncFailed")}</Alert>}
      {list && list.sync.unreadableFields.length > 0 && (
        <Alert severity="warning">
          {translate("financeNotifications.unreadableFields", { fields: list.sync.unreadableFields.join(", ") })}
        </Alert>
      )}
      {(error || moreFailed) && <Alert severity="error">{translate("financeNotifications.loadError")}</Alert>}
      {loading && !data && <CircularProgress aria-label={translate("financeNotifications.loading")} />}
      {list && list.nodes.length === 0 && (
        <Typography color="text.secondary">{translate("financeNotifications.empty")}</Typography>
      )}
      {list?.nodes.map(notification => (
        <FinanceNotificationCard key={notification.id} notification={notification} canDismiss={canDismiss === true} />
      ))}
      {list && (
        <CursorPagination
          page={page}
          perPage={PAGE_SIZE}
          itemCount={list.nodes.length}
          hasNextPage={hasNextPage}
          isPending={loading || loadingMore}
          resource={ResourceName.FinanceNotifications}
          onPageChange={next => void changePage(next)}
        />
      )}
    </Stack>
  );
};

// The open Finance notifications, narrowed to the rule named by the address.
const FinanceNotificationsRoute = () => {
  const [params, setParams] = useSearchParams();
  const param = params.get("rule");
  const rule = isFinanceNotificationRule(param) ? param : null;
  const changeRule = (next: FinanceNotificationRule | null) => setParams(next ? { rule: next } : {}, { replace: true });
  return <FinanceNotifications key={rule ?? "all"} rule={rule} onRuleChange={changeRule} />;
};

export const FinanceNotificationsPage = () => (
  <RequirePermission permission={ResourceName.FinanceNotifications}>
    <FinanceNotificationsRoute />
  </RequirePermission>
);
